import { useEffect, useState } from 'react';
import type { Zone } from '@vigilo/shared';
import { get } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_ZONES } from '../data/mock';
import { crowdQuery } from '../apiScope';

function unwrapZones(body: Zone[] | { data?: Zone[] }): Zone[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

export function useZones() {
  const { isMock } = useMockMode();
  const [zones, setZones] = useState<Zone[]>(() => (isMock ? [...MOCK_ZONES] : []));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (isMock) {
        setZones([...MOCK_ZONES]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const body = await get<Zone[] | { data?: Zone[] }>(`/geo/zones?${crowdQuery({ archiveStatus: 'all' })}`);
        if (!cancelled) setZones(unwrapZones(body));
      } catch {
        if (!cancelled) setError('Failed to load zones - check Core API');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [isMock]);

  return { zones, loading, error };
}
